// MF-67 — Colour picker for Journal config (types, severities): calm suggested swatches, a native
// custom picker and a validated hex input. Controlled: value = '#RRGGBB'; onChange(hex) only fires
// with a valid colour. `onValidChange(bool)` lets the form block Save while the hex is invalid.
import { useEffect, useState } from 'react'
import { TextField, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import FormField from './FormField'
import { PRESET_COLORS, isHex } from './colorTokens'
import { inputSx } from './formStyles'

const Swatch = ({ color, selected, onClick }) => (
  <button
    type='button'
    onClick={onClick}
    aria-label={`Use colour ${color}`}
    aria-pressed={selected}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: '50%',
      backgroundColor: color,
      cursor: 'pointer',
      border: '2px solid #fff',
      boxShadow: selected ? `0 0 0 2px ${color}` : '0 0 0 1px rgb(226, 232, 240)',
      color: '#fff',
      fontSize: 12
    }}
  >
    {selected ? <FontAwesomeIcon icon={['fal', 'check']} /> : null}
  </button>
)

const ColorField = ({
  value,
  onChange,
  onValidChange,
  label = 'Colour',
  help = 'Any colour works — Maestro always shows it softly with readable text.',
  hint = 'Pick a suggested colour or enter your school’s own.'
}) => {
  const [hexInput, setHexInput] = useState(value)
  useEffect(() => setHexInput(value), [value])

  const hexError = hexInput && !isHex(hexInput) ? 'Use a 6-digit hex colour, e.g. #2563EB' : ''
  useEffect(() => onValidChange?.(!hexError), [hexError, onValidChange])

  const pick = (c) => onChange(c.toUpperCase())
  const onHex = (raw) => {
    const v = (raw.startsWith('#') ? raw : `#${raw}`).slice(0, 7)
    setHexInput(v)
    if (isHex(v)) onChange(v.toUpperCase())
  }
  const isPreset = PRESET_COLORS.includes(value)

  return (
    <FormField label={label} help={help} error={hexError} hint={hint}>
      <div className='flex flex-wrap' style={{ gap: 10 }}>
        {PRESET_COLORS.map((c) => (
          <Swatch key={c} color={c} selected={value === c} onClick={() => pick(c)} />
        ))}
      </div>
      <div className='flex items-center' style={{ gap: 10, marginTop: 6 }}>
        <label
          title='Custom colour'
          style={{
            display: 'inline-flex',
            position: 'relative',
            width: 40,
            height: 40,
            borderRadius: 8,
            backgroundColor: value,
            cursor: 'pointer',
            border: '1px solid rgb(226, 232, 240)',
            boxShadow: !isPreset ? `0 0 0 2px #fff, 0 0 0 4px ${value}` : 'none'
          }}
        >
          <input
            type='color'
            value={value}
            onChange={(e) => pick(e.target.value)}
            aria-label='Custom colour'
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
          />
        </label>
        <TextField
          size='small'
          value={hexInput}
          error={Boolean(hexError)}
          onChange={(e) => onHex(e.target.value.trim())}
          inputProps={{ 'aria-label': 'Hex colour', maxLength: 7, style: { fontFamily: 'monospace' } }}
          sx={{ ...inputSx, width: 140 }}
        />
        <Typography style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>Custom</Typography>
      </div>
    </FormField>
  )
}

export default ColorField
